import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Users, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface EnhancedEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  course: {
    id: string;
    title: string;
    description: string;
    duration_hours: number;
    max_participants: number;
    sessions?: any[];
    prerequisites?: any[];
  };
  onEnrollmentComplete?: () => void;
}

export default function EnhancedEnrollmentDialog({ 
  open, 
  onOpenChange, 
  course,
  onEnrollmentComplete 
}: EnhancedEnrollmentDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string>('');

  const handleEnroll = async () => {
    if (!user) {
      toast.error('Please log in to enroll');
      return;
    }

    // Check prerequisites before enrolling
    if (hasPrerequisites) {
      const prerequisitesMet = await checkPrerequisites();
      if (!prerequisitesMet) {
        toast.error('You must complete all required prerequisites before enrolling');
        return;
      }
    }

    setLoading(true);
    try {
      const enrollmentData = {
        course_id: course.id,
        participant_id: user.id,
        status: 'enrolled',
        enrollment_type: 'individual',
        enrolled_by: user.id,
        ...(selectedSession && { session_id: selectedSession })
      };

      const { error } = await supabase
        .from('enrollments')
        .insert(enrollmentData);

      if (error) throw error;

      toast.success('Successfully enrolled in course!');
      onOpenChange(false);
      onEnrollmentComplete?.();
      
    } catch (error: any) {
      console.error('Error enrolling:', error);
      if (error.code === '23505') {
        toast.error('You are already enrolled in this course');
      } else {
        toast.error('Failed to enroll in course');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPrerequisites = async () => {
    try {
      const requiredPrereqs = course.prerequisites?.filter(p => p.is_required) || [];
      
      for (const prereq of requiredPrereqs) {
        if (prereq.prerequisite_type === 'course') {
          // Check if user has completed the prerequisite course
          const { data: completedEnrollment } = await supabase
            .from('enrollments')
            .select('status')
            .eq('participant_id', user!.id)
            .eq('course_id', prereq.prerequisite_course_id)
            .eq('status', 'completed')
            .single();
          
          if (!completedEnrollment) {
            return false;
          }
        }
        // Add other prerequisite type checks here if needed
      }
      
      return true;
    } catch (error) {
      console.error('Error checking prerequisites:', error);
      return false;
    }
  };

  const getAvailableSpots = (session: any) => {
    const enrolled = session.enrollments?.length || 0;
    const maxSpots = session.max_participants || course.max_participants || 0;
    return maxSpots - enrolled;
  };

  const hasPrerequisites = course.prerequisites && course.prerequisites.length > 0;
  const hasSessions = course.sessions && course.sessions.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Enroll in Course</DialogTitle>
          <DialogDescription>
            Complete your enrollment for "{course.title}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Course Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{course.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{course.description}</p>
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {course.duration_hours}h duration
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    Max {course.max_participants} participants
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prerequisites Warning */}
          {hasPrerequisites && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2 text-orange-800">
                  <AlertCircle className="h-4 w-4" />
                  Prerequisites Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  {course.prerequisites?.map((prereq) => (
                    <li key={prereq.id} className="text-sm text-orange-700">
                      • {prereq.prerequisite_description}
                      {prereq.is_required && <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Session Selection */}
          {hasSessions && (
            <div className="space-y-4">
              <Label>Select a Session</Label>
              {course.sessions!.length === 1 ? (
                // Auto-select if only one session
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-4">
                    {(() => {
                      const session = course.sessions![0];
                      setSelectedSession(session.id);
                      return (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {new Date(session.session_date).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {session.start_time} - {session.end_time}
                              </div>
                              {session.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  {session.location}
                                </div>
                              )}
                            </div>
                            <Badge variant="outline">
                              {getAvailableSpots(session)} spots left
                            </Badge>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {course.sessions!.map((session) => {
                    const spotsLeft = getAvailableSpots(session);
                    const isSelected = selectedSession === session.id;
                    
                    return (
                      <Card 
                        key={session.id} 
                        className={`cursor-pointer transition-colors ${
                          isSelected ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                        } ${spotsLeft === 0 ? 'opacity-50' : ''}`}
                        onClick={() => spotsLeft > 0 && setSelectedSession(session.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {new Date(session.session_date).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {session.start_time} - {session.end_time}
                              </div>
                              {session.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  {session.location}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {spotsLeft === 0 ? (
                                <Badge variant="destructive">Full</Badge>
                              ) : (
                                <Badge variant="outline">{spotsLeft} spots left</Badge>
                              )}
                              {isSelected && (
                                <Badge>Selected</Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleEnroll} 
            disabled={loading || (hasSessions && !selectedSession)}
          >
            {loading ? 'Enrolling...' : 'Confirm Enrollment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}